import { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import useScrollReveal from '../hooks/useScrollReveal';
import API from '../utils/api';

const PAGES = ['all', 'home', 'yourself', 'earth', 'stocks', 'generosity'];

const getVideoEmbedInfo = (url) => {
  const ytMatch = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/);
  if (ytMatch) {
    return {
      type: 'youtube',
      embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}`,
      thumbnail: `https://img.youtube.com/vi/${ytMatch[1]}/hqdefault.jpg`,
    };
  }
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return { type: 'vimeo', embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`, thumbnail: null };
  }
  return { type: 'direct', embedUrl: url, thumbnail: null };
};

const VideoCard = ({ video, onPlay, delay = 0 }) => {
  const info = getVideoEmbedInfo(video.url);
  const thumb = video.thumbnail || info.thumbnail;

  return (
    <div className="video-card reveal" style={{ transitionDelay: `${delay}ms` }} onClick={() => onPlay(video, info)}>
      <div className="video-card-thumb">
        {thumb
          ? <img src={thumb} alt={video.title} className="video-thumb-img" />
          : <div className="video-thumb-placeholder">▶</div>
        }
        <div className="video-play-overlay">
          <div className="video-play-btn">▶</div>
        </div>
        {video.featured && <span className="video-featured-badge">Featured</span>}
      </div>
      <div className="video-card-body">
        <span className="video-page-tag">{video.page}</span>
        <h3 className="video-card-title">{video.title}</h3>
        {video.description && <p className="video-card-desc">{video.description}</p>}
      </div>
    </div>
  );
};

const VideoModal = ({ video, info, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="video-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="video-modal">
        <button className="video-modal-close" onClick={onClose}>✕</button>
        <div className="video-embed-wrap">
          {info.type === 'youtube' || info.type === 'vimeo' ? (
            <iframe
              src={info.embedUrl + '?autoplay=1'}
              title={video.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="video-embed-frame"
            />
          ) : (
            <video controls autoPlay className="video-embed-frame">
              <source src={info.embedUrl} />
            </video>
          )}
        </div>
        <div className="video-modal-info">
          <h3 className="video-modal-title">{video.title}</h3>
          {video.description && <p className="video-modal-desc">{video.description}</p>}
        </div>
      </div>
    </div>
  );
};

const VideosPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [playing, setPlaying] = useState(null);
  useScrollReveal();

  useEffect(() => {
    API.get('/videos/page/all')
      .then(r => { setVideos(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = activeFilter === 'all'
    ? videos
    : videos.filter(v => v.page === activeFilter || v.page === 'all');

  return (
    <div>
      {/* Hero */}
      <div className="hero-placeholder" style={{
        background: 'linear-gradient(135deg, #1A1A1A 0%, #2C4A3E 50%, #1A1A1A 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', textAlign: 'center', gap: 16,
        padding: '80px 5%', minHeight: 280,
      }}>
        <div style={{ fontSize: '3rem' }}>▶</div>
        <div style={{ 
          fontSize: '0.75rem', fontWeight: 500, letterSpacing: '0.2em', 
          textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 4 
        }}>Village Media</div>
        <h1 style={{ 
          fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2rem, 5vw, 3.5rem)',
          color: 'white', fontWeight: 600, lineHeight: 1.1
        }}>Watch & Learn</h1>
        <p style={{ color: 'rgba(255,255,255,0.6)', maxWidth: 560, fontSize: '1rem' }}>
          Curated videos across all investment pillars — personal growth, sustainability, finance, and generosity.
        </p>
      </div>

      <section className="section">
        {/* Filter tabs */}
        <div className="videos-filter-tabs">
          {PAGES.map(p => (
            <button
              key={p}
              className={`videos-filter-btn ${activeFilter === p ? 'active' : ''}`}
              onClick={() => setActiveFilter(p)}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--warm-gray)' }}>
            <div style={{ fontSize: '3rem', marginBottom: 16, opacity: 0.3 }}>▶</div>
            <p style={{ fontSize: '1.1rem' }}>No videos in this category yet.</p>
            <p style={{ fontSize: '0.9rem', marginTop: 8 }}>The admin will add videos soon — check back later!</p>
          </div>
        ) : (
          <div className="videos-grid">
            {filtered.map((video, i) => (
              <VideoCard
                key={video._id}
                video={video}
                delay={i * 60}
                onPlay={(v, info) => setPlaying({ video: v, info })}
              />
            ))}
          </div>
        )}
      </section>

      {playing && (
        <VideoModal
          video={playing.video}
          info={playing.info}
          onClose={() => setPlaying(null)}
        />
      )}

      <Footer />
    </div>
  );
};

export default VideosPage;
